import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import apiClient from '@/services/api-client';
import { apiConfig } from '@/config/api';

interface User {
  id: string;
  username?: string;
  email?: string;
  is_admin?: boolean;
  is_super_admin?: boolean;
  roles?: string[];
  first_name?: string;
  last_name?: string;
  second_name?: string;
  job_title?: string;
  location?: string;
  photo_url?: string;
  created_at?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  changePassword: (oldPassword: string, newPassword: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      const userData = localStorage.getItem('userData');

      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          // Try to fetch fresh user data from /me endpoint
          try {
            const meResponse = await apiClient.get(apiConfig.endpoints.auth.me);
            const freshUserData = meResponse.data;
            
            // Map backend response to frontend User interface
            const updatedUser: User = {
              id: freshUserData.id,
              email: freshUserData.email,
              username: freshUserData.email?.split('@')[0] || parsedUser.username,
              roles: freshUserData.roles?.map((r: any) => r.name) || [],
              first_name: freshUserData.first_name,
              last_name: freshUserData.last_name,
              second_name: freshUserData.second_name,
              job_title: freshUserData.job_title,
              location: freshUserData.location,
              photo_url: freshUserData.photo_url,
              created_at: freshUserData.created_at,
              // Map roles to legacy fields for backward compatibility
              is_admin: freshUserData.roles?.some((r: any) => r.name === 'admin') || false,
              is_super_admin: freshUserData.roles?.some((r: any) => r.name === 'superadmin') || false,
            };
            
            setUser(updatedUser);
            localStorage.setItem('userData', JSON.stringify(updatedUser));
          } catch (meError) {
            console.warn('Failed to fetch user data from /me endpoint, using cached data:', meError);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
        }
      }

      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      // Backend expects OAuth2 password flow style form data
      // Using URLSearchParams for application/x-www-form-urlencoded (OAuth2 standard)
      const params = new URLSearchParams();
      params.append('username', username);
      params.append('password', password);
      params.append('grant_type', 'password');

      const response = await apiClient.post(
        apiConfig.endpoints.auth.login,
        params,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          withCredentials: true, // Ensure cookies are sent and received
        }
      );

      const data = response.data as any;
      
      // Log full response for debugging
      console.log('[AUTH] Login response:', {
        status: response.status,
        statusText: response.statusText,
        data: data,
        headers: response.headers,
      });

      // According to OpenAPI, response is Token { access_token, refresh_token, token_type }
      const token: string =
        data?.access_token || data?.token || data?.accessToken;

      if (!token) {
        console.error('Login response did not include an access token', {
          responseData: data,
          responseKeys: data ? Object.keys(data) : [],
        });
        return false;
      }
      
      console.log('[AUTH] Token extracted successfully');

      // Decode JWT token to get user info
      let decodedToken: any = null;
      try {
        decodedToken = jwtDecode(token);
        console.log('[AUTH] Decoded token:', decodedToken);
      } catch (error) {
        console.error('[AUTH] Failed to decode token:', error);
      }

      // Try to fetch user data from /me endpoint for accurate role information
      let userData: User = {
        id: decodedToken?.user_id || decodedToken?.sub || username,
        username: decodedToken?.username || username,
        email: decodedToken?.email || username,
        is_admin: decodedToken?.is_admin || false,
        is_super_admin: decodedToken?.is_super_admin || false,
      };

      // Fetch fresh user data from /me endpoint
      try {
        console.log('[AUTH] Fetching user data from /me endpoint...');
        const meResponse = await apiClient.get(apiConfig.endpoints.auth.me);
        const freshUserData = meResponse.data;
        console.log('[AUTH] /me endpoint response:', freshUserData);
        
        // Map backend response to frontend User interface
        userData = {
          id: freshUserData.id,
          email: freshUserData.email,
          username: freshUserData.email?.split('@')[0] || username,
          roles: freshUserData.roles?.map((r: any) => r.name) || [],
          first_name: freshUserData.first_name,
          last_name: freshUserData.last_name,
          second_name: freshUserData.second_name,
          job_title: freshUserData.job_title,
          location: freshUserData.location,
          photo_url: freshUserData.photo_url,
          created_at: freshUserData.created_at,
          // Map roles to legacy fields for backward compatibility
          is_admin: freshUserData.roles?.some((r: any) => r.name === 'admin') || false,
          is_super_admin: freshUserData.roles?.some((r: any) => r.name === 'superadmin') || false,
        };
        
        console.log('[AUTH] User data from /me endpoint:', userData);
      } catch (meError: any) {
        console.warn('[AUTH] Failed to fetch user data from /me endpoint, using token data:', {
          error: meError,
          message: meError?.message,
          response: meError?.response?.data,
          status: meError?.response?.status,
        });
        
        // Extract roles from token if /me fails
        if (decodedToken) {
          const roles: string[] = [];
          if (decodedToken.is_super_admin) roles.push('superadmin');
          if (decodedToken.is_admin) roles.push('admin');
          if (roles.length === 0) roles.push('user');
          userData.roles = roles;
        }
      }

      console.log('[AUTH] Final user data:', userData);

      localStorage.setItem('authToken', token);
      localStorage.setItem('userData', JSON.stringify(userData));

      setUser(userData);
      
      // Dispatch custom event to notify components (like Header) about user data update
      window.dispatchEvent(new Event('userDataUpdated'));
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    // Call backend logout endpoint, but always clear local state/token
    (async () => {
      try {
        await apiClient.post(apiConfig.endpoints.auth.logout);
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        setUser(null);
      }
    })();
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<boolean> => {
    try {
      console.log('[AUTH] Changing password...');
      const response = await apiClient.post(apiConfig.endpoints.auth.changePassword, {
        current_password: oldPassword,
        new_password: newPassword,
        confirm_new_password: newPassword,
      });
      
      console.log('[AUTH] Password change successful:', response.status, response.data);
      
      // After password change, we need to re-login to get new cookies with updated permissions
      // The user will need to login again with the new password
      // Clear current auth state
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      setUser(null);
      
      return true;
    } catch (error: any) {
      // Re-throw the error so components can handle it with proper error messages
      // Components will check the error response to determine the error type
      throw error;
    }
  };

  const value: AuthContextType = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    changePassword,
  }), [user, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
